// client/src/admin/instagram/FlowBuilder.js
import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  AccountTree as FlowIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Message as MessageIcon,
  CallSplit as ConditionIcon,
  Flag as TriggerIcon,
  ArrowForward as ArrowIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";

// ── Node type definitions — replace the existing NODE_TYPES in FlowBuilder.js
const NODE_TYPES = {
  trigger: {
    label: "Trigger",
    icon: TriggerIcon,
    color: "#10B981",
    desc: "When someone messages you",
    hasBranch: false,
  },
  send_message: {
    label: "Send Message",
    icon: MessageIcon,
    color: "#6366F1",
    desc: "Send a text reply",
    hasBranch: false,
  },
  follower_check: {
    label: "Follower Check",
    icon: ConditionIcon,
    color: "#E1306C",
    desc: "Branch: does this person follow you?",
    hasBranch: true,
    yesLabel: "✅ Follows you",
    noLabel: "❌ Doesn't follow",
  },
  condition: {
    label: "Keyword Check",
    icon: ConditionIcon,
    color: "#F59E0B",
    desc: "Branch: does message contain keyword?",
    hasBranch: true,
    yesLabel: "✅ Contains keyword",
    noLabel: "❌ Doesn't contain",
  },
};

const createNode = (type, position = { x: 0, y: 0 }) => ({
  id: `node_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  type,
  position,
  data: {
    type,
    message: type === "send_message" ? "Hello! Thanks for messaging us 👋" : "",
    keyword: type === "condition" ? "follow" : "",
    label: NODE_TYPES[type]?.label || type,
  },
});

// ── FlowNode — replace existing component in FlowBuilder.js
const FlowNode = ({
  node,
  onUpdate,
  onDelete,
  isLast,
  allNodes,
  edges,
  onSetBranchTarget,
}) => {
  const theme = useTheme();
  const nodeType = NODE_TYPES[node.data?.type] || NODE_TYPES.send_message;
  const Icon = nodeType.icon;
  const color = nodeType.color;

  // Find what nodes the yes/no handles connect to
  const yesEdge = edges.find(
    (e) =>
      e.source === node.id && (e.sourceHandle === "yes" || e.label === "yes"),
  );
  const noEdge = edges.find(
    (e) =>
      e.source === node.id && (e.sourceHandle === "no" || e.label === "no"),
  );
  const linearEdge = !nodeType.hasBranch
    ? edges.find((e) => e.source === node.id)
    : null;

  const getNodeLabel = (nodeId) => {
    const n = allNodes.find((x) => x.id === nodeId);
    return n ? n.data?.label || NODE_TYPES[n.data?.type]?.label || n.id : null;
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `2px solid ${alpha(color, 0.4)}`,
          borderRadius: 2,
          bgcolor: alpha(color, 0.04),
          transition: "all 0.15s",
          "&:hover": { borderColor: color, bgcolor: alpha(color, 0.07) },
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: alpha(color, 0.15),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: `1.5px solid ${alpha(color, 0.3)}`,
            }}
          >
            <Icon sx={{ fontSize: 18, color }} />
          </Box>

          <Box flex={1} minWidth={0}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color, fontSize: "0.82rem" }}
                >
                  {nodeType.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: "0.65rem" }}
                >
                  {nodeType.desc}
                </Typography>
              </Stack>
              {node.data?.type !== "trigger" && (
                <IconButton
                  size="small"
                  onClick={() => onDelete(node.id)}
                  sx={{
                    color: "text.disabled",
                    p: 0.4,
                    "&:hover": {
                      color: "error.main",
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>

            {/* ── send_message ── */}
            {node.data?.type === "send_message" && (
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                label="Message Text"
                placeholder="Type your reply..."
                value={node.data?.message || ""}
                onChange={(e) => onUpdate(node.id, { message: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    fontSize: "0.8rem",
                  },
                }}
              />
            )}

            {/* ── follower_check ── */}
            {node.data?.type === "follower_check" && (
              <Stack spacing={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  Automatically checks if the sender follows your Instagram
                  account. Connect the{" "}
                  <strong style={{ color: "#10B981" }}>✅ Yes</strong> and{" "}
                  <strong style={{ color: "#EF4444" }}>❌ No</strong> branches
                  below.
                </Typography>

                {/* YES branch */}
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha("#10B981", 0.3)}`,
                    bgcolor: alpha("#10B981", 0.05),
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: "#10B981", fontSize: "0.72rem" }}
                    >
                      ✅ YES — Follows you
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: "0.68rem" }}
                    >
                      {yesEdge
                        ? `→ ${getNodeLabel(yesEdge.target) || yesEdge.target}`
                        : "→ not connected"}
                    </Typography>
                  </Stack>
                </Box>

                {/* NO branch */}
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha("#EF4444", 0.3)}`,
                    bgcolor: alpha("#EF4444", 0.05),
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: "#EF4444", fontSize: "0.72rem" }}
                    >
                      ❌ NO — Doesn't follow
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: "0.68rem" }}
                    >
                      {noEdge
                        ? `→ ${getNodeLabel(noEdge.target) || noEdge.target}`
                        : "→ not connected"}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            )}

            {/* ── condition (keyword) ── */}
            {node.data?.type === "condition" && (
              <Stack spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="If message contains"
                  placeholder="e.g. follow, token, help"
                  value={node.data?.keyword || ""}
                  onChange={(e) =>
                    onUpdate(node.id, { keyword: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      fontSize: "0.8rem",
                    },
                  }}
                />
                <Stack direction="row" spacing={1}>
                  <Box
                    sx={{
                      flex: 1,
                      p: 0.75,
                      borderRadius: 1.5,
                      border: `1px solid ${alpha("#10B981", 0.3)}`,
                      bgcolor: alpha("#10B981", 0.05),
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: "#10B981", fontSize: "0.68rem" }}
                    >
                      ✅ YES — Contains keyword
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      sx={{ fontSize: "0.65rem" }}
                    >
                      {yesEdge
                        ? `→ ${getNodeLabel(yesEdge.target)}`
                        : "→ not connected"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 0.75,
                      borderRadius: 1.5,
                      border: `1px solid ${alpha("#EF4444", 0.3)}`,
                      bgcolor: alpha("#EF4444", 0.05),
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: "#EF4444", fontSize: "0.68rem" }}
                    >
                      ❌ NO — Doesn't contain
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      sx={{ fontSize: "0.65rem" }}
                    >
                      {noEdge
                        ? `→ ${getNodeLabel(noEdge.target)}`
                        : "→ not connected"}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            )}

            {/* ── trigger ── */}
            {node.data?.type === "trigger" && (
              <Typography variant="caption" color="text.secondary">
                Flow starts when someone sends you a DM on Instagram
              </Typography>
            )}

            {/* Linear next indicator */}
            {!nodeType.hasBranch && linearEdge && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                mt={0.75}
              >
                <ArrowIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: "0.65rem" }}
                >
                  → {getNodeLabel(linearEdge.target) || linearEdge.target}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Arrow connector between nodes */}
      {!isLast && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 0.25 }}>
          <Box
            sx={{ width: 2, height: 20, bgcolor: "divider", borderRadius: 1 }}
          />
        </Box>
      )}
    </Box>
  );
};

// ── FlowEditor — replace existing in FlowBuilder.js
const FlowEditor = ({ flow, onSave, onCancel }) => {
  const theme = useTheme();

  const [title, setTitle] = useState(flow?.title || "New Flow");
  const [nodes, setNodes] = useState(() => {
    if (flow?.flow_data) {
      try {
        const d =
          typeof flow.flow_data === "string"
            ? JSON.parse(flow.flow_data)
            : flow.flow_data;
        return d.nodes?.length ? d.nodes : [createNode("trigger")];
      } catch {
        return [createNode("trigger")];
      }
    }
    return [createNode("trigger")];
  });
  const [edges, setEdges] = useState(() => {
    if (flow?.flow_data) {
      try {
        const d =
          typeof flow.flow_data === "string"
            ? JSON.parse(flow.flow_data)
            : flow.flow_data;
        return d.edges || [];
      } catch {
        return [];
      }
    }
    return [];
  });
  const [addNodeType, setAddNodeType] = useState("send_message");

  // ── Connect nodes — branch nodes get yes/no edges, others get linear ──
  const rebuildEdges = useCallback((nodeList) => {
    const newEdges = [];

    for (let i = 0; i < nodeList.length - 1; i++) {
      const current = nodeList[i];
      const next = nodeList[i + 1];
      const currentType = NODE_TYPES[current.data?.type];

      if (currentType?.hasBranch) {
        // Branch node: look ahead for yes (i+1) and no (i+2) targets
        const yesTarget = nodeList[i + 1];
        const noTarget = nodeList[i + 2];

        if (yesTarget) {
          newEdges.push({
            id: `edge_${current.id}_yes`,
            source: current.id,
            target: yesTarget.id,
            sourceHandle: "yes",
            label: "yes",
          });
        }
        if (noTarget) {
          newEdges.push({
            id: `edge_${current.id}_no`,
            source: current.id,
            target: noTarget.id,
            sourceHandle: "no",
            label: "no",
          });
          i++; // Skip the no-target node from being connected linearly
        }
      } else {
        newEdges.push({
          id: `edge_${current.id}_${next.id}`,
          source: current.id,
          target: next.id,
          sourceHandle: null,
        });
      }
    }

    setEdges(newEdges);
  }, []);

  function handleAddNode() {
    const newNode = createNode(addNodeType);
    const newNodes = [...nodes, newNode];
    setNodes(newNodes);
    rebuildEdges(newNodes);
  }

  function handleDeleteNode(nodeId) {
    const newNodes = nodes.filter((n) => n.id !== nodeId);
    setNodes(newNodes);
    rebuildEdges(newNodes);
  }

  function handleUpdateNode(nodeId, updates) {
    setNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n,
      ),
    );
  }

  function handleSave() {
    onSave({
      flow_id: flow?.flow_id || null,
      title,
      flow_data: { nodes, edges },
      is_active: flow?.is_active || "yes",
    });
  }

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        label="Flow Name"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        size="small"
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
      />

      {/* Legend */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.info.main, 0.06),
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.7 }}
        >
          💡 <strong>How branching works:</strong> When you add a{" "}
          <strong>Follower Check</strong> or <strong>Keyword Check</strong>{" "}
          node, the next node added becomes the{" "}
          <strong style={{ color: "#10B981" }}>YES</strong> branch, and the one
          after becomes the <strong style={{ color: "#EF4444" }}>NO</strong>{" "}
          branch.
        </Typography>
      </Box>

      <Divider />

      {/* Node list */}
      <Box sx={{ maxHeight: 400, overflowY: "auto", pr: 0.5 }}>
        <Stack spacing={0}>
          {nodes.map((node, idx) => (
            <FlowNode
              key={node.id}
              node={node}
              onUpdate={handleUpdateNode}
              onDelete={handleDeleteNode}
              isLast={idx === nodes.length - 1}
              allNodes={nodes}
              edges={edges}
            />
          ))}
        </Stack>
      </Box>

      {/* Add node row */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel sx={{ fontSize: "0.8rem" }}>Add Node</InputLabel>
            <Select
              value={addNodeType}
              label="Add Node"
              onChange={(e) => setAddNodeType(e.target.value)}
              sx={{ fontSize: "0.8rem", borderRadius: 1.5 }}
            >
              {Object.entries(NODE_TYPES)
                .filter(([k]) => k !== "trigger")
                .map(([key, val]) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: "0.8rem" }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <val.icon sx={{ fontSize: 14, color: val.color }} />
                      <Box>
                        <Typography
                          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
                        >
                          {val.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.65rem", color: "text.disabled" }}
                        >
                          {val.desc}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={handleAddNode}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            Add
          </Button>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!title.trim()}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Save Flow
        </Button>
      </Stack>
    </Stack>
  );
};

// ── Main FlowBuilder Component ─────────────────────────────────────────────
const FlowBuilder = ({ lang: propLang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => propLang?.[k] || ctxLang?.[k] || k;

  const [flows, setFlows] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchFlows();
  }, []);

  async function fetchFlows() {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/instagram/admin/flows",
      admin: true,
      post: false,
      showLoading: false,
    });
    setLoading(false);
    if (res?.data?.success) setFlows(res.data.data || []);
  }

  async function handleSaveFlow(flowData) {
    const res = await hitAxios({
      path: "/api/instagram/admin/flows/save",
      admin: true,
      post: true,
      obj: flowData,
    });
    if (res?.data?.success) {
      setEditorOpen(false);
      setEditingFlow(null);
      fetchFlows();
    }
  }

  async function handleDeleteFlow(flow_id) {
    const res = await hitAxios({
      path: "/api/instagram/admin/flows/delete",
      admin: true,
      post: true,
      obj: { flow_id },
    });
    if (res?.data?.success) fetchFlows();
  }

  async function handleToggleActive(flow) {
    const newStatus = flow.is_active === "yes" ? "no" : "yes";
    let flowData = {};
    try {
      flowData =
        typeof flow.flow_data === "string"
          ? JSON.parse(flow.flow_data)
          : flow.flow_data;
    } catch {}
    await hitAxios({
      path: "/api/instagram/admin/flows/save",
      admin: true,
      post: true,
      obj: {
        flow_id: flow.flow_id,
        title: flow.title,
        flow_data: flowData,
        is_active: newStatus,
      },
      showSnackbar: false,
    });
    fetchFlows();
  }

  const getNodeCount = (flow) => {
    try {
      const d =
        typeof flow.flow_data === "string"
          ? JSON.parse(flow.flow_data)
          : flow.flow_data;
      return d?.nodes?.length || 0;
    } catch {
      return 0;
    }
  };

  return (
    <Box>
      <PageHeader
        title={t("flowBuilder") || "Flow Builder"}
        subtitle="Build automated Instagram DM reply flows"
        icon={FlowIcon}
        primaryAction={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingFlow(null);
              setEditorOpen(true);
            }}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            New Flow
          </Button>
        }
      />

      {flows.length === 0 && !loading ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <FlowIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography
            variant="body1"
            fontWeight={600}
            color="text.secondary"
            mb={1}
          >
            No flows yet
          </Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            Create your first Instagram DM automation flow
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingFlow(null);
              setEditorOpen(true);
            }}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Create Flow
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {flows.map((flow) => {
            const nodeCount = getNodeCount(flow);
            const isActive = flow.is_active === "yes";

            return (
              <Paper
                key={flow.flow_id}
                elevation={0}
                sx={{
                  p: 2.5,
                  border: `1px solid ${isActive ? alpha(theme.palette.success.main, 0.4) : theme.palette.divider}`,
                  borderRadius: 2,
                  bgcolor: isActive
                    ? alpha(theme.palette.success.main, 0.03)
                    : "background.paper",
                  transition: "all 0.15s",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FlowIcon sx={{ fontSize: 20, color: "primary.main" }} />
                    </Box>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="text.primary"
                        >
                          {flow.title}
                        </Typography>
                        <Chip
                          label={isActive ? "Active" : "Inactive"}
                          size="small"
                          color={isActive ? "success" : "default"}
                          sx={{ fontSize: "0.65rem", height: 18 }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.disabled">
                        {nodeCount} nodes · Updated{" "}
                        {new Date(flow.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Tooltip title={isActive ? "Deactivate" : "Activate"}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(flow)}
                        sx={{
                          color: isActive ? "success.main" : "text.disabled",
                          "&:hover": {
                            bgcolor: alpha(
                              isActive
                                ? theme.palette.success.main
                                : theme.palette.text.primary,
                              0.08,
                            ),
                          },
                        }}
                      >
                        {isActive ? (
                          <PlayIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <StopIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingFlow(flow);
                          setEditorOpen(true);
                        }}
                        sx={{
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFlow(flow.flow_id)}
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Flow Editor Dialog */}
      <CommonDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingFlow(null);
        }}
        title={editingFlow ? "Edit Flow" : "Create New Flow"}
        subtitle="Build your Instagram DM automation"
        icon={FlowIcon}
        maxWidth="sm"
      >
        <FlowEditor
          flow={editingFlow}
          onSave={handleSaveFlow}
          onCancel={() => {
            setEditorOpen(false);
            setEditingFlow(null);
          }}
        />
      </CommonDialog>
    </Box>
  );
};

export default FlowBuilder;
